import { useCallback, useContext, useMemo } from 'react';
import CurrentUserContext from '../../contexts/CurrentUserContext';
import getScorecardIdsInApp from './getScorecardIdsInApp';
import getReportIdsInApp from './getReportIdsInApp';
import getDashboardIdsInApp from './getDashboardIdsInApp';
import { V3AppsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import RolesContext from '../../contexts/RolesContext';
import PERMISSIONS from '../../permissionDefinitions';
import { GroupsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/GroupsProvider';
import ImpersonatorContext from '../../contexts/ImpersonatorContext';
import getBoardIdsInApp from './getPerformanceBoardIdsInApp';

const useV3HasAccess = () => {
  const currentUser = useContext(CurrentUserContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const { groupsCurrentUserIsIn } = useContext(GroupsContext);
  const { currentPermissions } = useContext(RolesContext);
  const { availableProductApps, availableWorkspaces } =
    useContext(V3AppsContext);
  const hasViewAllPublishedContentPermission = useMemo(() => {
    return currentPermissions.includes(
      PERMISSIONS.CONTENT_ACCESS.VIEW_ALL_PUBLISHED,
    );
  }, [currentPermissions]);
  const hasViewAllPrivateContentPermission = useMemo(() => {
    return currentPermissions.includes(
      PERMISSIONS.CONTENT_ACCESS.VIEW_ALL_PRIVATE,
    );
  }, [currentPermissions]);

  const availableApps = useMemo(() => {
    return [...availableProductApps, ...availableWorkspaces];
  }, [availableProductApps, availableWorkspaces]);

  const currentUserId = useMemo(() => {
    if (impersonatorId) {
      return impersonatorId;
    }

    return currentUser.id;
  }, [currentUser.id, impersonatorId]);

  const hasDirectAccess = useCallback(
    ({ v3Access }: { v3Access: V3Access }) => {
      return (
        v3Access.isPublic ||
        v3Access.userIds.includes(currentUserId) ||
        groupsCurrentUserIsIn.some((g) => v3Access.groupIds.includes(g.id))
      );
    },
    [currentUserId, groupsCurrentUserIsIn],
  );

  const appHasAccess = useCallback(
    ({
      type,
      typeId,
      app,
    }: {
      type: 'dashboard' | 'report' | 'goal' | 'scorecard' | 'board';
      typeId: string;
      app: V3.App;
    }): boolean => {
      if (
        type === 'scorecard' &&
        getScorecardIdsInApp({ app }).includes(typeId)
      ) {
        return true;
      }

      if (type === 'report' && getReportIdsInApp({ app }).includes(typeId)) {
        return true;
      }

      if (
        type === 'dashboard' &&
        getDashboardIdsInApp({ app }).includes(typeId)
      ) {
        return true;
      }

      if (type === 'board' && getBoardIdsInApp({ app }).includes(typeId)) {
        return true;
      }

      return false;
    },
    [],
  );

  const hasAppAccess = useCallback(
    ({
      type,
      typeId,
    }: {
      type: 'dashboard' | 'report' | 'goal' | 'scorecard' | 'board';
      typeId: string;
    }): boolean => {
      const availableInApps = availableApps.filter((app) =>
        appHasAccess({ app, type, typeId }),
      );

      if (availableInApps.length === 0) {
        return false;
      }

      return true;
    },
    [availableApps, appHasAccess],
  );

  const v3HasAccess = useCallback(
    ({
      resource,
      type,
      typeId,
    }: {
      resource:
        | GeneralGoal
        | Scorecards.Scorecard
        | DashboardType
        | ReportType
        | PerformanceBoardTypes.Board
        | Board;
      type: 'dashboard' | 'report' | 'goal' | 'scorecard' | 'board';
      typeId: string;
    }): boolean => {
      if ('createdBy' in resource && resource.createdBy === currentUserId) {
        return true;
      }

      if (hasViewAllPrivateContentPermission) {
        return true;
      }

      if (!resource.isV3Published) {
        return false;
      }

      if (resource.isV3Published && hasViewAllPublishedContentPermission) {
        return true;
      }

      if (hasAppAccess({ typeId, type })) {
        return true;
      }

      if (hasDirectAccess({ v3Access: resource.v3Access })) {
        return true;
      }

      return false;
    },
    [
      currentUserId,
      hasViewAllPrivateContentPermission,
      hasViewAllPublishedContentPermission,
      hasAppAccess,
      hasDirectAccess,
    ],
  );

  return v3HasAccess;
};

export default useV3HasAccess;
